import { inject, Injectable } from '@angular/core'
import { ResourceJSON, ResourceQueryJSON } from '@monorepo/next-lab'
import { BaseStorageService } from './base-storage.service'
import { lastValueFrom } from 'rxjs'
import { ResourcesService } from '@monorepo/golab-generated'

@Injectable({
  providedIn: 'root',
})
export class MongoStorageService extends BaseStorageService {
  private readonly resourcesService = inject(ResourcesService)

  override async createResourcePromise(resource: ResourceJSON): Promise<ResourceJSON> {
    if (this.debug) {
      console.log('createResourcePromise', resource)
    }
    this.validateResource(resource, false)

    const clonedResource = this.cloneResource(resource)

    const createdResource = await lastValueFrom(
      this.resourcesService.resourceControllerCreateResource({
        alwaysAllowActor: false,
        body: { metadata: clonedResource.metadata, content: clonedResource.content },
      }),
    )
    return { metadata: createdResource.metadata as any, content: createdResource.content }
  }

  override async deleteResourcePromise(resourceId: string): Promise<void> {
    if (this.debug) {
      console.log('deleteResourcePromise', resourceId)
    }

    await lastValueFrom(this.resourcesService.resourceControllerDeleteResource({ resourceId }))
  }

  override async queryResourcesPromise(query: ResourceQueryJSON, _fetchFromServer: boolean): Promise<ResourceJSON[]> {
    if (this.debug) {
      console.log('queryResourcesPromise', query)
      console.log('queryResourcesPromise', this.providerId)
    }
    this.validateQuery(query)
    const clonedQuery = this.cloneJSON(query)
    this.setDefaultQueryProperties(clonedQuery)

    const foundResources = await lastValueFrom(
      this.resourcesService.resourceControllerQueryResources({
        providerId: this.providerId,
        ...clonedQuery,
      }),
    )
    return foundResources as any[]
  }

  override async readResourcePromise(resourceId: string, _fetchFromServer: boolean): Promise<ResourceJSON> {
    if (this.debug) {
      console.log('readResourcePromise', resourceId)
    }

    const resource = await lastValueFrom(
      this.resourcesService.resourceControllerReadResource({ resourceId: resourceId }),
    )
    return { metadata: resource.metadata as any, content: resource.content }
  }

  override async updateResourcePromise(resource: ResourceJSON, async: boolean | undefined): Promise<ResourceJSON> {
    if (this.debug) {
      console.log('updateResourcePromise', resource)
    }
    this.validateResource(resource, true)

    const clonedResource = this.cloneResource(resource)

    const updatedResource = await lastValueFrom(
      this.resourcesService.resourceControllerUpdateResource({
        resourceId: resource.metadata.target.id,
        body: { metadata: clonedResource.metadata, content: clonedResource.content },
      }),
    )

    return { metadata: updatedResource.metadata as any, content: updatedResource.content }
  }

  override async resourceExistsPromise(resourceId: string, _fetchFromServer: boolean): Promise<boolean> {
    if (this.debug) {
      console.log('resourceExistsPromise', resourceId)
    }

    const result = await lastValueFrom(
      this.resourcesService.resourceControllerResourceExists({
        ilsId: this.providerId,
        resourceId: resourceId,
      }),
    )
    return result
  }

  override getDebugLabel(): string {
    return `MongoStorageService`
  }
}
